import { Checkbox, Grid, Stack, FormControl, IconButton, InputAdornment, InputLabel, ListItemText, MenuItem, Select, SelectChangeEvent, TextField, styled } from '@mui/material';
import { ChangeEvent, useEffect, useState } from 'react';
import { useAppSelector } from '../../app/hooks';
import CloseIcon from '@mui/icons-material/Close';
import { TransactionModel } from '../../gql-types.generated';
import { Viewer } from "../../util/Constants";
import { selectTransactionList } from '../../features/EDIContainer/EDIContainerSlice';
import { DateTime } from 'luxon';
import { DateTimePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterLuxon } from '@mui/x-date-pickers/AdapterLuxon';

import FilterBar from "./FilterBar";
import { getLocalStorageItem, removeLocalStorageItem, setLocalStorageItem } from '../../util/ViewerUtility';

const DatePickerWrapper = styled('div')((props) => ({
    width: '100%',
}));

interface MapsFilterBarProps {
    id?: string;
    className?: string;
    loading: boolean;
    visible: boolean;
    viewer: Viewer | undefined;
    onClose: () => void;
    onFilterChanged: (filterVersion: string | undefined, filterDescription: string | undefined, filterTransactionIds: string[], filterFileName: string | undefined, filterDateFrom: DateTime | undefined, filterDateTo: DateTime | undefined) => void;
}

const MapsFilterBar: React.FC<MapsFilterBarProps> = props => {
    const { id, className, loading = false, visible = false, viewer, onFilterChanged } = props;
    const [filterVersion, setFilterVersion] = useState<string | undefined>();
    const [filterDescription, setFilterDescription] = useState<string | undefined>();
    const [filterTransactionNames, setFilterTransactionNames] = useState<string[]>([]);
    const [filterTransactionIds, setFilterTransactionIds] = useState<string[]>();
    const [filterFileName, setFilterFileName] = useState<string | undefined>(undefined);
    const [filterDateFrom, setFilterDateFrom] = useState<DateTime | undefined>(undefined);
    const [filterDateTo, setFilterDateTo] = useState<DateTime | undefined>(undefined);
    const fieldsDisabled = visible && loading;

    useEffect(() => {
        // filters from local storage to be remembered across sessions
        let version = getLocalStorageItem(viewer, "mapFilterVersion");
        if (version != null) {
            setFilterVersion(version);
        }

        let desc = getLocalStorageItem(viewer, "mapFilterDescription");
        if (desc != null) {
            setFilterDescription(desc);
        }

        let transactionIds = getLocalStorageItem(viewer, "mapFilterTransactionIds");
        let parsedTransactionIds;
        if (transactionIds != null) {
            parsedTransactionIds = JSON.parse(transactionIds);
            setFilterTransactionIds(parsedTransactionIds);
        }

        let transactionNames = getLocalStorageItem(viewer, "mapFilterTransactionNames");
        if (transactionNames != null && transactionNames.length > 0) {
            let parsedTransactionNames = JSON.parse(transactionNames);
            setFilterTransactionNames(parsedTransactionNames);
        }

        let fileName = getLocalStorageItem(viewer, "mapFilterFileName");
        if (fileName != null) {
            setFilterFileName(fileName);
        }

        let dateFromStr = getLocalStorageItem(viewer, "mapFilterDateFrom");
        let parsedDateFrom;
        if (dateFromStr != null) {
            parsedDateFrom = DateTime.fromISO(dateFromStr);
            setFilterDateFrom(parsedDateFrom);
        }

        let dateToStr = getLocalStorageItem(viewer, "mapFilterDateTo");
        let parsedDateTo;
        if (dateToStr != null) {
            parsedDateTo = DateTime.fromISO(dateToStr);
            setFilterDateTo(parsedDateTo);
        }

        // Always trigger onFilterChanged with current filter values
        onFilterChanged(version as string | undefined, desc as string | undefined, parsedTransactionIds ?? [], fileName as string | undefined, parsedDateFrom, parsedDateTo);
        
    }, []);

    const transactions = useAppSelector(selectTransactionList);

    const onSearchClick = () => {
        onFilterChanged(filterVersion, filterDescription, filterTransactionIds ?? [], filterFileName, filterDateFrom, filterDateTo);
    };

    const onCloseClick = () => {
        // remove any filters
        clearFilters();

        // call parent to hide bar
        props.onClose();

        // trigger a new search with filters undefined since state of filter values might not be updated yet
        onFilterChanged(undefined, undefined, [], undefined, undefined, undefined);
    };

    const clearFilters = () => {
        clearVersionFilter();
        clearDescriptionFilter();
        removeLocalStorageItem(viewer, "mapFilterTransactionNames");
        setFilterTransactionNames([]);
        removeLocalStorageItem(viewer, "mapFilterTransactionIds");
        setFilterTransactionIds([]);
        clearFileNameFilter();
        setFilterDateFrom(undefined);
        removeLocalStorageItem(viewer, "mapFilterDateFrom");
        setFilterDateTo(undefined);
        removeLocalStorageItem(viewer, "mapFilterDateTo");
    };

    const clearVersionFilter = () => {
        removeLocalStorageItem(viewer, "mapFilterVersion");
        setFilterVersion(undefined);
    };

    const clearDescriptionFilter = () => {
        removeLocalStorageItem(viewer, "mapFilterDescription");
        setFilterDescription(undefined);
    };

    const clearFileNameFilter = () => {
        setFilterFileName(undefined);
        removeLocalStorageItem(viewer, "mapFilterFileName");
    };

    const SecondaryMenuItemProps = {
        display: 'block',
        width: '240px',
        textOverflow: "ellipsis",
        overflow: 'hidden',
    };

    const getTransactionsFilterList = () => {
        if (transactions && transactions.length) {
            return (
                transactions.map((transaction: TransactionModel) => (
                    <MenuItem
                        key={transaction.id}
                        value={transaction.name}
                        disabled={fieldsDisabled}
                    >
                        <Checkbox checked={filterTransactionNames && filterTransactionNames.indexOf(transaction.name) > -1} />
                        <ListItemText primary={transaction.name} secondary={transaction.description} secondaryTypographyProps={SecondaryMenuItemProps} />
                    </MenuItem>
                ))
            );
        }
        return null;
    };

    const onVersionFilterChanged = (event: ChangeEvent<HTMLInputElement>) => {
        let changedValue = event.target.value;
        if (changedValue?.length > 0) {
            setLocalStorageItem(viewer, "mapFilterVersion", changedValue);
        }
        else {
            removeLocalStorageItem(viewer, "mapFilterVersion");
        }
        setFilterVersion(changedValue);
    };

    const onDescriptionFilterChanged = (event: ChangeEvent<HTMLInputElement>) => {
        let changedValue = event.target.value;
        if (changedValue?.length > 0) {
            setLocalStorageItem(viewer, "mapFilterDescription", changedValue);
        }
        else {
            removeLocalStorageItem(viewer, "mapFilterDescription");
        }
        setFilterDescription(changedValue);
    };

    const onTransactionFilterChanged = (event: SelectChangeEvent<typeof filterTransactionNames>) => {
        const {
            target: { value },
        } = event;
        // On autofill we get a stringified value.
        let nameArray = typeof value === 'string' ? value.split(',') : value;

        if (nameArray.length > 0) {
            setLocalStorageItem(viewer, "mapFilterTransactionNames", JSON.stringify(nameArray));
        }
        else {
            removeLocalStorageItem(viewer, "mapFilterTransactionNames");
        }
        setFilterTransactionNames(nameArray);

        // the array from the multiselect only contains the names
        // get the associated ids to actually send as a filter to the query
        let ids: string[] = [];
        let selectedTransactions = transactions?.filter(b => nameArray.includes(b.name));
        if (selectedTransactions && selectedTransactions.length > 0) {
            selectedTransactions.forEach(transaction => {
                ids.push(transaction.id);
            });
        }
        if (ids.length > 0) {
            setLocalStorageItem(viewer, "mapFilterTransactionIds", JSON.stringify(ids));
        }
        else {
            removeLocalStorageItem(viewer, 'mapFilterTransactionIds');
        }

        setFilterTransactionIds(ids);
    };

    const onFileNameFilterChange = (event: ChangeEvent<HTMLInputElement>) => {
        let nameValue = event.target.value;
        if (nameValue?.length > 0) {
            setLocalStorageItem(viewer, "mapFilterFileName", nameValue);
        }
        else {
            removeLocalStorageItem(viewer, "mapFilterFileName");
        }
        setFilterFileName(nameValue);
    };

    const onDateFromFilterChange = (value: DateTime | null) => {
        let newDateFrom = value ?? undefined;
        if (newDateFrom) {
            setLocalStorageItem(viewer, "mapFilterDateFrom", newDateFrom.toISO());
        }
        else {
            removeLocalStorageItem(viewer, "mapFilterDateFrom");
        }
        setFilterDateFrom(newDateFrom);
        
        let newDateTo = newDateFrom?.endOf('month') ?? undefined;
        if (newDateTo) {
            setLocalStorageItem(viewer, "mapFilterDateTo", newDateTo.toISO());
        }
        else {
            removeLocalStorageItem(viewer, "mapFilterDateTo");
        }
        setFilterDateTo(newDateTo);
    };

    const onDateToFilterChange = (value: DateTime | null) => {
        let newDateTo = value ?? undefined;
        if (newDateTo) {
            setLocalStorageItem(viewer, "mapFilterDateTo", newDateTo.toISO());
        }
        else {
            removeLocalStorageItem(viewer, "mapFilterDateTo");
        }
        setFilterDateTo(newDateTo);
    };

    const dropDownMenuProps = {
        PaperProps: {
            style: {
                maxHeight: '300px',
            },
        },
    };

    const versionFilterProps = {
        endAdornment: (
            <InputAdornment position="end">
                <IconButton
                    aria-label="clear version filter"
                    onClick={clearVersionFilter}
                    disabled={fieldsDisabled}
                >
                    {filterVersion && filterVersion.length > 0 ? <CloseIcon fontSize='small' sx={{ padding: '2px' }} /> : null}
                </IconButton>
            </InputAdornment>
        )
    };

    const descriptionFilterProps = {
        endAdornment: (
            <InputAdornment position="end">
                <IconButton
                    aria-label="clear description filter"
                    onClick={clearDescriptionFilter}
                    disabled={fieldsDisabled}
                >
                    {filterDescription && filterDescription.length > 0 ? <CloseIcon fontSize='small' sx={{ padding: '2px' }} /> : null}
                </IconButton>
            </InputAdornment>
        )
    };

    return (
        <FilterBar id={id} className={className} visible={visible} onClose={onCloseClick} onSearch={onSearchClick} barHeight="140px">
            <Stack direction={'column'} justifyContent="center" rowGap={2} width="100%" >
                <Grid container item direction={'row'} ml={{xs:1, sm:3, md:6, lg:10, xl:14}} columnGap={2} justifyContent="flex-start" xs={12}>
                    <Grid item xs={3} xl={3}>
                        <TextField
                            itemID="map-filter-description"
                            fullWidth
                            value={filterDescription ?? ''}
                            label="Description"
                            disabled={fieldsDisabled}
                            inputProps={{ 'aria-label': 'description', 'maxLength': 50, }}
                            InputProps={descriptionFilterProps}
                            onChange={onDescriptionFilterChanged}
                            autoComplete="off"
                            data-cy="map-description-filter"
                            variant="standard"
                        />
                    </Grid>
                    <Grid item xs={3} xl={3}>
                        <LocalizationProvider dateAdapter={AdapterLuxon}>
                            <DatePickerWrapper>
                                <DateTimePicker
                                    label="Last Modified Date From"
                                    value={filterDateFrom ?? null}
                                    views={['year','month','day','hours','minutes']}
                                    openTo={'month'}
                                    disabled={fieldsDisabled}
                                    onChange={onDateFromFilterChange}
                                    slotProps={{
                                        textField: {
                                            variant: "standard",
                                            fullWidth: true,
                                            inputProps: {'aria-label': 'date from'}
                                        },
                                        field: { clearable: true }
                                    }}
                                    data-cy="map-filter-date-from"
                                ></DateTimePicker>
                            </DatePickerWrapper>
                        </LocalizationProvider>
                    </Grid>
                    <Grid item xs={3} xl={3}>
                        <LocalizationProvider dateAdapter={AdapterLuxon}>
                            <DatePickerWrapper>
                                <DateTimePicker
                                    label="Last Modified Date To"
                                    value={filterDateTo ?? null}
                                    views={['year','month','day','hours','minutes']}
                                    openTo={'day'}
                                    minDateTime={filterDateFrom}
                                    disabled={fieldsDisabled}
                                    onChange={onDateToFilterChange}
                                    slotProps={{
                                        textField: {
                                            variant: "standard",
                                            fullWidth: true,
                                            inputProps: {'aria-label': 'date to'}
                                        },
                                        field: { clearable: true }
                                    }}
                                    data-cy="map-filter-date-to"
                                ></DateTimePicker>
                            </DatePickerWrapper>
                        </LocalizationProvider>
                    </Grid>
                </Grid>
                <Grid container item direction={'row'} ml={{xs:1, sm:3, md:6, lg:10, xl:14}} columnGap={2} xs={12} justifyContent="flex-start">
                    <Grid item xs={3} xl={3}>
                        <FormControl variant="standard" fullWidth disabled={fieldsDisabled}>
                            <InputLabel id="map-filter-transaction-label">Transaction</InputLabel>
                            <Select
                                labelId="map-filter-transaction-label"
                                aria-labelledby="map-filter-transaction-label"
                                id="map-filter-transaction"
                                multiple
                                value={filterTransactionNames ?? []}
                                MenuProps={dropDownMenuProps}
                                onChange={onTransactionFilterChanged}
                                renderValue={(selected) => selected.join(', ')}
                                data-cy="map-transaction-filter"
                            >
                                {getTransactionsFilterList()}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={3} xl={3}>
                        <TextField
                            itemID="map-filter-file-name"
                            fullWidth
                            value={filterFileName ?? ''}
                            label="File Name"
                            disabled={fieldsDisabled}
                            inputProps={{ 'aria-label': 'file name', 'maxLength': 50, }}
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton
                                            aria-label="clear file name filter"
                                            onClick={clearFileNameFilter}
                                            disabled={fieldsDisabled}
                                        >
                                            {filterFileName && filterFileName.length > 0 ? <CloseIcon fontSize='small' sx={{ padding: '2px' }} /> : null}
                                        </IconButton>
                                    </InputAdornment>
                                )
                            }}
                            onChange={onFileNameFilterChange}
                            autoComplete="off"
                            data-cy="map-file-name-filter"
                            variant="standard"
                        />
                    </Grid>
                    <Grid item xs={3} xl={3}>
                        <TextField
                            itemID="map-filter-version"
                            fullWidth
                            value={filterVersion ?? ''}
                            label="EDI Standard Version"
                            disabled={fieldsDisabled}
                            inputProps={{ 'aria-label': 'version', 'maxLength': 50, }}
                            InputProps={versionFilterProps}
                            onChange={onVersionFilterChanged}
                            autoComplete="off"
                            data-cy="map-name-filter"
                            variant="standard"
                        />
                    </Grid>
                </Grid>
            </Stack>
        </FilterBar>
    );
};

export default MapsFilterBar;